import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useShell } from '../../shell/ShellContext';
import { leagueMeta, mergeLeagueRollovers } from '../../shell/leagueUtils';
import { rateMyTradeAPI, monteCarloAPI } from '../../utils/api';
import { SleeperAvatar } from '../../shell/SleeperAvatar';

/**
 * SimulateHome — the in-shell /simulate surface, rebuilt per the shell design
 * doctrine (.claude/SHELL-REDESIGN-PLAN.md): app-scale league picker, no
 * marketing hero. Logic carried over from MonteCarloLanding (league click
 * params, saved-scenario restore links, any-username search); presentation
 * is shell-native.
 */

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const LeagueAvatar = ({ avatar, name }) => (
  <SleeperAvatar id={avatar} name={name} size={40} radius="10px" />
);

/**
 * Saved-scenario names are auto-generated piece mashes:
 * ' · ' separates trades, '/' separates pieces within a trade
 * (e.g. "2027 1st · Williams/Williams · Beck/Love/2027 1st/2027 2nd").
 * Flatten to a piece list; null/empty names yield no pieces.
 */
const parseScenarioPieces = (name) => {
  if (!name || !String(name).trim()) return [];
  return String(name)
    .split(' · ')
    .flatMap((trade) => trade.split('/'))
    .map((piece) => piece.trim())
    .filter(Boolean);
};

const MAX_SCENARIO_CHIPS = 4;

const SavedScenarioCard = ({ sim, league, onOpen }) => {
  const leagueName = league?.name || sim.league_name || 'Simulation';
  const pieces = parseScenarioPieces(sim.name);
  const shown = pieces.slice(0, MAX_SCENARIO_CHIPS);
  const extra = pieces.length - shown.length;
  const date = fmtDate(sim.createdAt || sim.created_at);
  return (
    <button
      className="ft-resume"
      onClick={onOpen}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', minHeight: 128 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <SleeperAvatar id={league?.avatar} name={leagueName} size={32} radius="9px" />
        <span
          className="ft-k"
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {leagueName}
        </span>
      </div>
      {pieces.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {shown.map((piece, i) => (
            <span key={`${piece}-${i}`} className="ft-chip outline">{piece}</span>
          ))}
          {extra > 0 && (
            <span className="ft-chip outline" style={{ color: 'var(--ft-ink-3)' }}>+{extra} more</span>
          )}
        </div>
      ) : (
        <div className="t" style={{ marginTop: 12 }}>Untitled scenario</div>
      )}
      <div style={{ marginTop: 'auto', paddingTop: 12, fontSize: 12, color: 'var(--ft-ink-3)' }}>
        {date ? `Saved ${date}` : 'Resume'}
      </div>
    </button>
  );
};

const LeagueGrid = ({ leagues, onPick }) => (
  <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))' }}>
    {leagues.map((league) => (
      <button key={league.leagueId} className="ft-leaguecard" onClick={() => onPick(league)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <LeagueAvatar avatar={league.avatar} name={league.name} />
          <div style={{ minWidth: 0 }}>
            <div className="nm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {league.name}
            </div>
            <div className="tm" style={{ marginBottom: 0 }}>{leagueMeta(league)}</div>
          </div>
        </div>
      </button>
    ))}
  </div>
);

const SimulateHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sleeperUser, leagues, leaguesLoading } = useShell();

  const [savedSims, setSavedSims] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState(null); // { user, leagues }
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    monteCarloAPI
      .listSimulations()
      .then((res) => { if (active) setSavedSims(res.data?.simulations || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [user]);

  // Map every known league id (including rolled-over prior seasons) to the
  // current league object so saved sims can show the league avatar. Sims from
  // leagues outside the user's account simply miss and fall back to initials.
  const leagueById = useMemo(() => {
    const map = new Map();
    (leagues || []).forEach((league) => {
      map.set(league.leagueId, league);
      (league.previousSeasons || []).forEach((prev) => {
        if (!map.has(prev.leagueId)) map.set(prev.leagueId, league);
      });
    });
    return map;
  }, [leagues]);

  const pickLeague = (league, owner) => {
    const params = new URLSearchParams();
    if (league.name) params.set('name', league.name);
    if (owner?.userId) params.set('userId', owner.userId);
    if (owner?.username) params.set('username', owner.username.toLowerCase());
    navigate(`/simulate/${league.leagueId}?${params.toString()}`);
  };

  const openSavedSim = (sim) => {
    const params = new URLSearchParams({ sim: sim.id });
    if (sleeperUser?.username) params.set('username', sleeperUser.username.toLowerCase());
    navigate(`/simulate/${sim.sleeper_league_id}?${params.toString()}`);
  };

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const name = query.trim().replace(/^@/, '');
    if (!name) return;
    setSearchLoading(true);
    setSearchError(null);
    try {
      const res = await rateMyTradeAPI.getUserLeagues(name);
      setSearched({
        user: res.data.user,
        leagues: mergeLeagueRollovers(res.data.leagues || []),
      });
    } catch {
      setSearchError('No Sleeper user by that name.');
    } finally {
      setSearchLoading(false);
    }
  };

  const signedInWithSleeper = !!sleeperUser?.username;

  return (
    <div className="ft-view">
      <Helmet>
        <title>Simulate — Fantasy Taco</title>
        <meta name="description" content="Run Monte Carlo simulations on your dynasty league. See how trades impact your playoff odds." />
      </Helmet>

      <span className="ft-k brand">Simulate</span>
      <h1 className="ft-hero">{signedInWithSleeper ? 'Pick a league.' : 'Simulate any season.'}</h1>
      <p className="ft-sub" style={{ maxWidth: 640 }}>
        {signedInWithSleeper
          ? 'Real player projections and betting-market odds play out the rest of your season 100,000 times. Then you build trades against it and watch your odds move.'
          : 'Real player projections and betting-market odds play your season out 100,000 times — every matchup, every week, through the playoffs. Then you make trades and see how your chances move.'}
      </p>
      {!signedInWithSleeper && (
        <div className="ft-card" style={{ maxWidth: 560, marginTop: 24 }}>
          <span className="ft-k">What you walk away with</span>
          <div style={{ display: 'grid', gap: 9, margin: '14px 0 2px' }}>
            {[
              ['Make playoffs', 72, null],
              ['Win it all', 18, null],
              ['After the trade you’re weighing', 81, '+9'],
            ].map(([label, pct, delta]) => (
              <div key={label} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 220px) 1fr 64px', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 12.5, color: 'var(--ft-ink-3)' }}>{label}</span>
                <span style={{ height: 8, borderRadius: 980, background: '#FFFFFF', overflow: 'hidden', display: 'block' }}>
                  <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: 'var(--ft-brand)', borderRadius: 980 }} />
                </span>
                <span style={{ fontSize: 12.5, fontWeight: 650, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {pct}%
                  {delta ? <span style={{ color: 'var(--ft-green-ink)', fontWeight: 700 }}> {delta}</span> : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Searched user's leagues take over the main grid */}
      {searched ? (
        <div className="ft-section">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span className="ft-k">Leagues for {searched.user?.username || 'manager'}</span>
            <button
              className="ft-fpill"
              onClick={() => { setSearched(null); setQuery(''); setSearchOpen(false); }}
            >
              {signedInWithSleeper ? 'Back to your leagues' : 'Clear'}
            </button>
          </div>
          {searched.leagues.length === 0 ? (
            <p className="ft-sub" style={{ marginTop: 14 }}>No dynasty leagues found.</p>
          ) : (
            <div style={{ marginTop: 4 }}>
              <LeagueGrid leagues={searched.leagues} onPick={(l) => pickLeague(l, searched.user)} />
            </div>
          )}
        </div>
      ) : signedInWithSleeper ? (
        <div className="ft-section">
          <span className="ft-k">Your leagues</span>
          {leaguesLoading ? (
            <div className="ft-loading">Loading your leagues…</div>
          ) : leagues.length === 0 ? (
            <p className="ft-sub" style={{ marginTop: 14 }}>No dynasty leagues found for your Sleeper account.</p>
          ) : (
            <div style={{ marginTop: 4 }}>
              <LeagueGrid leagues={leagues} onPick={(l) => pickLeague(l, sleeperUser)} />
            </div>
          )}
        </div>
      ) : null}

      {/* Saved scenarios */}
      {!searched && savedSims.length > 0 && (
        <div className="ft-section">
          <span className="ft-k">Saved scenarios</span>
          <div className="ft-rowcards">
            {savedSims.slice(0, 6).map((sim) => (
              <SavedScenarioCard
                key={sim.id}
                sim={sim}
                league={leagueById.get(sim.sleeper_league_id)}
                onOpen={() => openSavedSim(sim)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Any-username search — primary when anonymous, secondary when signed in */}
      <div className="ft-section">
        {signedInWithSleeper && !searchOpen && !searched ? (
          <button className="ft-fpill" onClick={() => setSearchOpen(true)}>
            Simulate another manager's league
          </button>
        ) : (!searched || searchError) && (
          <>
            {!signedInWithSleeper && <span className="ft-k">Whose leagues are we pulling</span>}
            <form onSubmit={runSearch} style={{ display: 'flex', gap: 10, marginTop: 10, maxWidth: 480 }}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Sleeper username"
                autoFocus={searchOpen}
                style={{
                  flex: 1, background: 'var(--ft-surface)', border: 'none',
                  borderRadius: 'var(--ft-r-input)', padding: '13px 16px', fontSize: 15, outline: 'none',
                }}
              />
              <button type="submit" className="ft-btn sm" disabled={searchLoading}>
                {searchLoading ? 'Looking…' : 'Find leagues'}
              </button>
            </form>
            {searchError && (
              <p className="ft-sub" style={{ marginTop: 10, fontSize: 13.5 }}>{searchError}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SimulateHome;
